import { SyntheticEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { CaseDocumentAdmin } from "pwapi";

import { fetchAdminCaseDocuments } from "shared/actions/admin/adminCaseDocumentActions";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { ControlledToggleButton } from "components/customInputs/buttons/ToggleButton";
import DateRange from "components/customInputs/DateRangeInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";
import {
  FilterObject,
  reduceFiltersForSearchParams,
  reduceFiltersToApi,
  resetFilters,
} from "components/filtersUtils";

import { FilterComponentProps } from "../types/adminTypes";

const DocumentsFilters = ({
  sortBy,
  pageSize,
  pageIndex,
  setPage,
}: FilterComponentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const usp = new URLSearchParams(location.search);
  const initializeFilters = (filters: FilterObject) => {
    return Object.keys(filters).reduce((acc: FilterObject, value) => {
      const initialFilter = usp.get(value);
      acc[value] = initialFilter ? initialFilter : "";
      return acc;
    }, {});
  };

  const [documentsFilters, setDocumentsFilters] = useState<FilterObject>(
    initializeFilters({
      createdAtBefore: "",
      createdAtAfter: "",
      category: "",
      status: "",
      caseAccession: "",
    })
  );

  const [isLooseQuery, setIsLooseQuery] = useState<boolean>(
    usp.get("isLoose") === "true"
  );

  useEffect(() => {
    filterDocuments(documentsFilters, isLooseQuery);
  }, [sortBy, pageSize, pageIndex]);

  useEffect(() => {
    setSearchParams(documentsFilters, isLooseQuery);
  }, [sortBy, pageIndex]);

  const onChangeFilter = (key: string, value: string) => {
    setDocumentsFilters({ ...documentsFilters, [key]: value });
  };

  const onChangeDateRangeFilter = (values: { [key: string]: Date }) => {
    setDocumentsFilters(Object.assign({}, documentsFilters, values));
  };

  const setSearchParams = (filters: FilterObject, isLoose: boolean) => {
    const params = {
      ...reduceFiltersForSearchParams(filters),
      ...(isLoose && { isLoose: true }),
    } as any;
    if (pageIndex > 0) {
      params.page = pageIndex + 1;
    }
    if (sortBy.length) {
      params.sortBy = sortBy.map((s) => `${s.desc ? "-" : ""}${s.id}`);
    }
    navigate(`${location.pathname}?${new URLSearchParams(params).toString()}`, {
      replace: true,
    });
  };

  const filterDocuments = (filters: FilterObject, isLoose: boolean) => {
    dispatch(
      fetchAdminCaseDocuments({
        filters: {
          limit: pageSize,
          offset: pageSize * pageIndex,
          ordering: sortBy.length
            ? `${sortBy[0].desc ? "-" : ""}${sortBy[0].id}`
            : "-created_at",
          ...reduceFiltersToApi(filters),
          ...(isLoose && { isLoose: "true" }),
        },
      })
    );
  };

  const onSubmitFilter = (e: SyntheticEvent) => {
    e.preventDefault();
    setSearchParams(documentsFilters, isLooseQuery);
    filterDocuments(documentsFilters, isLooseQuery);
    setPage && setPage(0);
  };

  const onResetFilter = () => {
    const newFilters = resetFilters(documentsFilters);
    setDocumentsFilters(newFilters);
    setIsLooseQuery(false);
    setSearchParams(newFilters, false);
    filterDocuments(newFilters, false);
    setPage && setPage(0);
  };

  return (
    <form className="AdminFilters" onSubmit={onSubmitFilter}>
      <div className="AdminFilters AdminFilters--vertical">
        <div className="AdminFilters__filters">
          <TextInput
            id="caseAccession"
            value={documentsFilters.caseAccession as string}
            label="Case Accession"
            onChange={(e: any) =>
              onChangeFilter("caseAccession", e.target.value)
            }
          />
          <SelectInput
            id="status"
            value={documentsFilters.status as string}
            label="Status"
            defaultLabel="All Statuses"
            options={Object.values(CaseDocumentAdmin.status).map((v) => ({
              label: v,
              value: v,
            }))}
            onChange={(e: any) => onChangeFilter("status", e.target.value)}
          />
          <SelectInput
            id="category"
            value={documentsFilters.category as string}
            label="Category"
            defaultLabel="All Categories"
            options={Object.values(CaseDocumentAdmin.category).map((v) => ({
              label: v.replaceAll("-", " "),
              value: v,
            }))}
            onChange={(e: any) => onChangeFilter("category", e.target.value)}
          />
          <DateRange
            id="createdAt"
            label="Created At"
            values={{
              from: {
                id: "createdAtAfter",
                name: "created_at_after",
                value: documentsFilters.createdAtAfter as string,
              },
              to: {
                id: "createdAtBefore",
                name: "created_at_before",
                value: documentsFilters.createdAtBefore as string,
              },
            }}
            onChangeDate={onChangeDateRangeFilter}
          />
        </div>
      </div>
      <div className="AdminFilters__actions AdminFilters__actions--content">
        <ControlledToggleButton
          isToggled={isLooseQuery}
          onClick={() => {
            setIsLooseQuery(!isLooseQuery);
            setSearchParams(documentsFilters, !isLooseQuery);
            filterDocuments(documentsFilters, !isLooseQuery);
          }}
        >
          Loose Documents
        </ControlledToggleButton>
        <div className="AdminFilters__actions--content">
          <PathologyWatchButton
            type="submit"
            color="green"
            title="filter cases"
          >
            Filter Documents
          </PathologyWatchButton>
          <PathologyWatchButton onClick={onResetFilter} title="reset filters">
            Reset Filters
          </PathologyWatchButton>
        </div>
      </div>
    </form>
  );
};

export default DocumentsFilters;
